import { useState, useEffect } from "react";
import { FaTimes, FaPlus } from "react-icons/fa";
import { useKeycloak } from "@react-keycloak/web";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import { createFloor, createRoom, getRoomTypes } from "../api/locations";
import { useLocationsPage } from "../state/locations";
import { useProjectsStore } from "../state/projects";
import { createSafeCloseHandler } from "../utils/modalUtils";
import './CompactRoomModal.css';

interface CompactRoomModalProps {
  onClose: () => void;
  projectId?: string;
  siteId?: string;
  title?: string;
}

export const CompactRoomModal = ({ onClose, projectId, siteId, title = "Create New Room" }: CompactRoomModalProps) => {
  const { keycloak: { token } } = useKeycloak();
  const { loadFloors, loadRooms } = useLocationsPage();
  const params = useParams<{ projectId: string }>();
  const projects = useProjectsStore((state) => state.projects);
  const [isLoading, setIsLoading] = useState(false);
  const [roomTypes, setRoomTypes] = useState<RoomTypeInterface[]>([]);
  const [phoneError, setPhoneError] = useState<string>('');
  
  const [form, setForm] = useState({
    selectedProjectId: projectId || params.projectId || '',
    selectedSiteId: siteId || '',
    floorName: '',
    roomName: '',
    roomType: '',
    area: '',
    roomIdentifier: '',
    directions: '',
    accessRequirements: '',
    accessContactsName: '',
    accessContactsSurname: '',
    contactPhone: '',
    contactEmail: ''
  });

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        console.log('🚪 Escape key pressed, closing modal...');
        onClose();
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Only show warning if user has entered data
      if (form.roomName.trim()) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [onClose, form.roomName]);

  // Load room types on component mount
  useEffect(() => {
    const loadRoomTypes = async () => {
      if (!token) return;
      
      try {
        const types = await getRoomTypes(token);
        setRoomTypes(Array.isArray(types) ? types : []);
      } catch (error) {
        console.error('Failed to load room types:', error);
        setRoomTypes([]);
      }
    };

    loadRoomTypes();
  }, [token]);

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    form, 
    onClose, 
    ['selectedProjectId', 'selectedSiteId']
  );

  // Simple close handler for Cancel button
  const handleCancel = () => {
    console.log('🚪 Cancel button clicked, closing modal...');
    onClose();
  };

  // Get available sites for selected project
  const selectedProject = projects.find(p => p.id === form.selectedProjectId);
  const availableSites = selectedProject?.sites || [];

  // Automatically select first site if available
  useEffect(() => {
    if (availableSites.length > 0 && !form.selectedSiteId) {
      setForm(prev => ({
        ...prev,
        selectedSiteId: availableSites[0].site_id || availableSites[0].id || ''
      }));
    }
  }, [availableSites, form.selectedSiteId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.roomName.trim()) {
      toast.error('Room name is required');
      return;
    }

    if (!form.selectedProjectId) {
      toast.error('Please select a project');
      return;
    }

    if (!form.selectedSiteId) {
      toast.error('Please select a site');
      return;
    }

    // Validate phone number if provided
    if (form.contactPhone && !validatePhone(form.contactPhone)) {
      toast.error('Please enter a valid international phone number');
      setPhoneError('Please enter a valid international phone number (e.g., +1234567890, +441234567890)');
      return;
    }

    setIsLoading(true);

    try {
      // First create floor
      let floorId = '';
      if (form.floorName.trim()) {
        const floorResponse = await createFloor(token || '', form.selectedProjectId, form.selectedSiteId, {
          project_id: form.selectedProjectId,
          site_id: form.selectedSiteId,
          name: form.floorName.trim()
        });
        floorId = floorResponse.id || '';
        await loadFloors(form.selectedProjectId, form.selectedSiteId);
      } else {
        const floorResponse = await createFloor(token || '', form.selectedProjectId, form.selectedSiteId, {
          project_id: form.selectedProjectId,
          site_id: form.selectedSiteId,
          name: 'Default Floor'
        });
        floorId = floorResponse.id || '';
        await loadFloors(form.selectedProjectId, form.selectedSiteId);
      }

      // Then create room
      if (floorId) {
        const roomData = {
          project_id: form.selectedProjectId,
          site_id: form.selectedSiteId,
          floor_id: floorId,
          name: form.roomName.trim(),
          type: form.roomType.trim() || undefined,
          area: form.area ? parseFloat(form.area) : 0,
          room_identifier: form.roomIdentifier.trim() || undefined,
          directions: form.directions.trim() || undefined,
          access_requirements: form.accessRequirements.trim() || undefined,
          access_contacts_name: form.accessContactsName.trim() || undefined,
          access_contacts_surname: form.accessContactsSurname.trim() || undefined,
          contact_phone: form.contactPhone.trim() || undefined,
          contact_email: form.contactEmail.trim() || undefined,
          access_updated_at: new Date().toISOString(),
          cabinet_name: form.roomName.trim(),
          cabinet_location: '',
          room_dimensions: '',
          rack_type: 'Standard Server Rack',
          power_circuit: 'Primary Power Circuit A',
          row_number: '0',
          cold_hot_aisle: 'Cold Aisle Front, Hot Aisle Back',
          rows: []
        };

        await createRoom(token || '', form.selectedProjectId, form.selectedSiteId, floorId, roomData);
        await loadRooms(form.selectedProjectId, form.selectedSiteId, floorId);
        
        window.dispatchEvent(new CustomEvent('roomCreated'));
        toast.success(`Room "${form.roomName}" created successfully!`);
        onClose();
        
        // Optional: Navigate to new room (commented out to prevent navigation issues)
        // if (createdRoom.id) {
        //   navigate(`/projects/${form.selectedProjectId}/sites/${form.selectedSiteId}/floors/${floorId}/rooms/${createdRoom.id}`);
        // }
      }
    } catch (error) {
      console.error('Error creating room:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create room. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to validate international phone number
  const validatePhone = (phone: string): boolean => {
    if (!phone || phone.trim() === '') {
      return true; // Empty is valid (optional field)
    }
    const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
    // International format: + followed by 1-3 digit country code and 4-14 digits
    // This allows: +1 (US), +44 (UK), +49 (Germany), etc.
    const internationalPhoneRegex = /^\+[1-9]\d{7,14}$/;
    return internationalPhoneRegex.test(cleanPhone);
  };

  const handleInputChange = (field: string, value: string) => {
    // Sanitize phone number - remove "tel:" prefix if present
    let sanitizedValue = value;
    if (field === 'contactPhone' && typeof value === 'string') {
      sanitizedValue = value.replace(/^tel:/i, '').trim();
      
      // Validate phone number
      if (sanitizedValue && !validatePhone(sanitizedValue)) {
        setPhoneError('Please enter a valid international phone number (e.g., +1234567890, +441234567890)');
      } else {
        setPhoneError('');
      }
    }
    
    setForm(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
  };

  const handleProjectChange = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    const firstSite = project?.sites?.[0];
    
    setForm(prev => ({
      ...prev,
      selectedProjectId: projectId,
      selectedSiteId: firstSite?.site_id || firstSite?.id || ''
    }));
  };

  return (
    <div className="compact-room-modal-overlay" onClick={handleSafeClose}>
      <button
        onClick={() => handleSafeClose()}
        className="compact-room-modal-close"
      >
        <FaTimes />
      </button>
      
      <div className="compact-room-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="compact-room-modal-header">
          <h2 className="compact-room-modal-title">{title}</h2>
        </div>

        <form onSubmit={handleSubmit} className="compact-room-modal-form">
          {/* Project Selection */}
          <div className="compact-room-modal-field">
            <label className="compact-room-modal-label">Project *</label>
            <select
              value={form.selectedProjectId}
              onChange={(e) => handleProjectChange(e.target.value)}
              required
              className="compact-room-modal-select"
            >
              <option value="">Select a project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Site Selection */}
          {form.selectedProjectId && (
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Site *</label>
              <select
                value={form.selectedSiteId}
                onChange={(e) => handleInputChange('selectedSiteId', e.target.value)}
                required
                className="compact-room-modal-select"
              >
                <option value="">Select a site</option>
                {availableSites.map((site) => (
                  <option key={site.site_id || site.id} value={site.site_id || site.id}>
                    {site.name} {site.address && `- ${site.address}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Floor and Room Names */}
          <div className="compact-room-modal-grid2">
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Floor Name</label>
              <input
                type="text"
                value={form.floorName}
                onChange={(e) => handleInputChange('floorName', e.target.value)}
                placeholder="Enter floor name"
                className="compact-room-modal-input"
              />
            </div>
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Room Name *</label>
              <input
                type="text"
                value={form.roomName}
                onChange={(e) => handleInputChange('roomName', e.target.value)}
                placeholder="Enter room name"
                required
                className="compact-room-modal-input"
              />
            </div>
          </div>

          {/* Room Type and Area */}
          <div className="compact-room-modal-grid2">
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Room Type</label>
              <select
                value={form.roomType}
                onChange={(e) => handleInputChange('roomType', e.target.value)}
                className="compact-room-modal-select"
              >
                <option value="">Select room type...</option>
                {Array.isArray(roomTypes) && roomTypes.map((type) => (
                  <option key={type.id} value={type.value}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Area (sq ft)</label>
              <input
                type="number"
                value={form.area}
                onChange={(e) => handleInputChange('area', e.target.value)}
                placeholder="Enter area"
                className="compact-room-modal-input"
              />
            </div>
          </div>

          {/* Room Identifier */}
          <div className="compact-room-modal-field">
            <label className="compact-room-modal-label">Room Identifier</label>
            <input
              type="text"
              value={form.roomIdentifier}
              onChange={(e) => handleInputChange('roomIdentifier', e.target.value)}
              placeholder="e.g., R-101, SR-A"
              className="compact-room-modal-input"
            />
          </div>

          {/* Directions */}
          <div className="compact-room-modal-field">
            <label className="compact-room-modal-label">Directions</label>
            <textarea
              value={form.directions}
              onChange={(e) => handleInputChange('directions', e.target.value)}
              placeholder="Enter directions to the room"
              rows={2}
              className="compact-room-modal-textarea"
            />
          </div>

          {/* Access Requirements */}
          <div className="compact-room-modal-field">
            <label className="compact-room-modal-label">Access Requirements</label>
            <textarea
              value={form.accessRequirements}
              onChange={(e) => handleInputChange('accessRequirements', e.target.value)}
              placeholder="Enter access requirements (key cards, permissions, etc.)"
              rows={2}
              className="compact-room-modal-textarea"
            />
          </div>

          {/* Contact Information */}
          <div className="compact-room-modal-grid2">
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Contact Phone</label>
              <input
                type="tel"
                value={form.contactPhone}
                onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                placeholder="+1234567890 or +441234567890"
                className={`compact-room-modal-input ${phoneError ? 'border-red-500' : ''}`}
              />
              {phoneError && (
                <p className="text-red-500 text-xs mt-1">{phoneError}</p>
              )}
              <p className="text-[#888] text-xs mt-1">International format with country code</p>
            </div>
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Contact Email</label>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                placeholder="Contact email"
                className="compact-room-modal-input"
              />
            </div>
          </div>

          {/* Access Contacts */}
          <div className="compact-room-modal-grid2">
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Contact First Name</label>
              <input
                type="text"
                value={form.accessContactsName}
                onChange={(e) => handleInputChange('accessContactsName', e.target.value)}
                placeholder="First name"
                className="compact-room-modal-input"
              />
            </div>
            <div className="compact-room-modal-field">
              <label className="compact-room-modal-label">Contact Last Name</label>
              <input
                type="text"
                value={form.accessContactsSurname}
                onChange={(e) => handleInputChange('accessContactsSurname', e.target.value)}
                placeholder="Last name"
                className="compact-room-modal-input"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="compact-room-modal-buttons">
            <button
              type="button"
              onClick={handleCancel}
              className="compact-room-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !form.roomName.trim() || !form.selectedProjectId || !form.selectedSiteId}
              className={`compact-room-modal-submit ${isLoading || !form.roomName.trim() || !form.selectedProjectId || !form.selectedSiteId ? 'disabled' : ''}`}
            >
              {isLoading ? (
                <>
                  <FaPlus className="loading" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <FaPlus />
                  <span>Create Room</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
